import {useEffect, useRef, useState} from "react"
import {Loader2, Plus, X} from "lucide-react"
import {upload} from '@vercel/blob/client';

import {FormCard} from "@/components/FormCard"
import {Button} from "@/components/ui/button"
import {Textarea} from "@/components/ui/textarea"
import {cn} from "@/lib/utils"
import {deleteOtkDocumentFileWithRetry, getOtkDocumentFilesWithUrl} from "@/lib/api/documents"
import {
    isPhotoCategoryVisible,
    MAX_PHOTO_BYTES,
    photoCategories,
    photoGroups,
    photosStateFromFiles,
    type PhotosTabState,
    type PhotoUpload,
    type YesNo,
} from "@/lib/documentEditor"
import {makeFilePath, type PhotoCategoryId, type UploadClientPayload} from "shared";

/**
 * document-editor.md's DE7 Фото tab. Deferred, flagged rather than silently
 * dropped: `cropperjs`-backed crop/rotate step and the canvas-burned
 * upload-date stamp (`PhotoCropDialog`, DE-crop) — both need a new npm
 * dependency this pass didn't add. A picked photo is stored directly as its
 * own local blob URL preview instead of going through a crop step first.
 */
function toPhotoUpload(file: File): PhotoUpload {
  const url = URL.createObjectURL(file)
  const error =
    file.size > MAX_PHOTO_BYTES ? "Файл завеликий (макс. 5 МБ)" : undefined
  return {
    name: file.name,
    size: file.size,
    url,
    error,
    uploading: error === undefined,
  }
}

function PhotoTile({
  photo,
  note,
  onNoteChange,
  onRemove,
  removeLabel,
  onViewFullscreen,
}: {
  photo: PhotoUpload
  note: string
  onNoteChange: (value: string) => void
  onRemove: () => void
  removeLabel: string
  onViewFullscreen: () => void
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div
        className={cn(
          "flex h-14 w-full items-center gap-2 rounded-lg border p-1.5",
          photo.error && "border-destructive"
        )}
      >
        <button
          className="h-full shrink-0 cursor-pointer overflow-hidden rounded-md"
          onClick={onViewFullscreen}
          type="button"
        >
          <img
            alt=""
            className="h-full w-11 object-cover"
            src={photo.url}
          />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium">{photo.name}</p>
          <p
            className={cn(
              "truncate text-xs text-muted-foreground",
              photo.error && "text-destructive"
            )}
          >
            {photo.error ??
              (photo.uploading
                ? "Завантаження…"
                : `${Math.round(photo.size / 1024)} КБ`)}
          </p>
        </div>
        {photo.uploading ? (
          <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
        ) : (
          <Button
            aria-label={removeLabel}
            onClick={onRemove}
            size="icon-xs"
            type="button"
            variant="ghost"
          >
            <X />
          </Button>
        )}
      </div>
      <Textarea
        className="h-14 max-h-14 min-h-14 resize-none overflow-hidden text-xs"
        maxLength={2000}
        onChange={(e) => onNoteChange(e.target.value)}
        placeholder="Примітка (до 2000 символів)"
        rows={2}
        value={note}
      />
    </div>
  )
}

function CategoryCell({
  categoryId,
  label,
  required,
  photo,
  note,
  onPick,
  onRemove,
  onNoteChange,
  onViewFullscreen,
}: {
  /** DOM id — `FormChapterValidationAlert`'s scroll/focus target for this category's upload-error message. */
  categoryId: string
  label: string
  required: boolean
  photo: PhotoUpload | undefined
  note: string
  onPick: (file: File) => void
  onRemove: () => void
  onNoteChange: (value: string) => void
  onViewFullscreen: () => void
}) {
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div
      className={cn(
        "rounded-lg",
        dragOver && "ring-2 ring-primary"
      )}
      id={`photo-${categoryId}`}
      onDragLeave={() => setDragOver(false)}
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        const file = e.dataTransfer.files?.[0]
        if (file) onPick(file)
      }}
      tabIndex={-1}
    >
      <p className="mb-1.5 text-xs leading-4 font-medium">
        {required && (
          <span aria-hidden="true" className="mr-0.5 shrink-0 text-destructive">
            *
          </span>
        )}
        {label}
      </p>
      {photo ? (
        <PhotoTile
          note={note}
          onNoteChange={onNoteChange}
          onRemove={onRemove}
          onViewFullscreen={onViewFullscreen}
          photo={photo}
          removeLabel={`Видалити фото: ${label}`}
        />
      ) : (
        <Button
          className="h-14 w-full"
          onClick={() => inputRef.current?.click()}
          type="button"
          variant="outline"
        >
          <Plus data-icon="inline-start" />
          Додати фото
        </Button>
      )}
      <input
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) onPick(file)
          e.target.value = ""
        }}
        ref={inputRef}
        type="file"
      />
    </div>
  )
}

function AdditionalCell({
  photos,
  onAdd,
  onRemove,
  onViewFullscreen,
}: {
  photos: PhotoUpload[]
  onAdd: (files: FileList) => void
  onRemove: (index: number) => void
  onViewFullscreen: (url: string) => void
}) {
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div
      className={cn("flex flex-col gap-3 rounded-lg", dragOver && "ring-2 ring-primary")}
      onDragLeave={() => setDragOver(false)}
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        if (e.dataTransfer.files?.length) onAdd(e.dataTransfer.files)
      }}
    >
      <p className="text-xs leading-4 font-medium">
        Додаткові фотографії (можна кілька)
      </p>
      {photos.map((photo, index) => (
        <div className="flex h-14 items-center gap-2 rounded-lg border p-1.5" key={photo.url}>
          <button
            className="h-full shrink-0 cursor-pointer overflow-hidden rounded-md"
            onClick={() => onViewFullscreen(photo.url)}
            type="button"
          >
            <img alt="" className="h-full w-11 object-cover" src={photo.url} />
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium">
              Додаткове фото {index + 1}
            </p>
            <p
              className={cn(
                "truncate text-xs text-muted-foreground",
                photo.error && "text-destructive"
              )}
            >
              {photo.error ??
                (photo.uploading
                  ? "Завантаження…"
                  : `${Math.round(photo.size / 1024)} КБ`)}
            </p>
          </div>
          {photo.uploading ? (
            <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
          ) : (
            <Button
              aria-label={`Видалити додаткове фото ${index + 1}`}
              onClick={() => onRemove(index)}
              size="icon-xs"
              type="button"
              variant="ghost"
            >
              <X />
            </Button>
          )}
        </div>
      ))}
      <Button
        className="h-14 w-full"
        onClick={() => inputRef.current?.click()}
        type="button"
        variant="outline"
      >
        <Plus data-icon="inline-start" />
        Додати фото
      </Button>
      <input
        accept="image/*"
        className="hidden"
        multiple
        onChange={(e) => {
          if (e.target.files?.length) onAdd(e.target.files)
          e.target.value = ""
        }}
        ref={inputRef}
        type="file"
      />
    </div>
  )
}

export function PhotosTab({
  value,
  onChange,
  vehicleCategory,
  hasTachograph,
  subjectId,
  documentId,
}: {
  value: PhotosTabState
  onChange: (patch: Partial<PhotosTabState>) => void
  vehicleCategory: string
  hasTachograph: YesNo
  subjectId?: string
  documentId?: string
}) {
  const [fullscreen, setFullscreen] = useState<string | null>(null)

  // Kept in sync with `value` so the async upload/delete continuations below
  // always merge against the latest state instead of the stale snapshot
  // captured when the async call started.
  const valueRef = useRef(value)
  useEffect(() => {
    valueRef.current = value
  }, [value])

  // Hydrates previews for files already stored server-side (reopening a
  // draft) — `OtkDocumentDetail` doesn't carry uploaded file/URL data, so
  // this tab fetches it separately. Runs once per `documentId`, not on
  // every local edit.
  const loadedDocumentIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!documentId || loadedDocumentIdRef.current === documentId) return
    loadedDocumentIdRef.current = documentId
    let cancelled = false
    void (async () => {
      try {
        const { files } = await getOtkDocumentFilesWithUrl(documentId)
        if (cancelled) return
        const loaded = photosStateFromFiles(files)
        onChange({
          photos: { ...loaded.photos, ...valueRef.current.photos },
          additional: [...valueRef.current.additional, ...loaded.additional],
        })
      } catch (error) {
        console.error("Не вдалося завантажити фотоматеріали документа", error)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId])

  // Vercel Blob's `addRandomSuffix` means the blob's real pathname (what
  // `DELETE /:id/files/:pathname` needs) is only known once `upload()`
  // resolves, not from the path requested up front.
  async function uploadFile(
    subjectId: string,
    documentId: string,
    category: PhotoCategoryId,
    file: File
  ): Promise<{ pathname?: string; uploading: false; error?: string }> {
    try {
      const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer())
      const sha256 = Array.from(new Uint8Array(digest), (byte) =>
        byte.toString(16).padStart(2, "0")
      ).join("")
      const pathname = makeFilePath(subjectId, documentId, category, file.name)
      const result = await upload(pathname, file, {
        multipart: false,
        access: "private",
        handleUploadUrl: `/api/otk/documents/${documentId}/files`,
        clientPayload: JSON.stringify({
          property: category,
          fileName: file.name,
          size: file.size,
          sha256,
        } satisfies UploadClientPayload),
      })
      return { pathname: result.pathname, uploading: false }
    } catch {
      return { uploading: false, error: "Не вдалося завантажити файл" }
    }
  }

  // If a file gets removed (or replaced) while its upload is still in
  // flight, the blob storage PUT may still succeed and get recorded
  // server-side afterward — this cleans up that now-orphaned file instead
  // of leaving it referenced on a document the user no longer sees.
  function discardIfOrphaned(pathname: string | undefined) {
    if (pathname && documentId) {
      deleteOtkDocumentFileWithRetry(documentId, pathname).catch((error) => {
        console.error("Не вдалося видалити файл документа", error)
      })
    }
  }

  async function pick(category: PhotoCategoryId, file: File) {
    if (!subjectId || !documentId) {
      throw new Error("Subject or document ID is missing");
    }

    const placeholder = toPhotoUpload(file)
    onChange({ photos: { ...valueRef.current.photos, [category]: placeholder } })
    if (placeholder.error) return

    const update = await uploadFile(subjectId, documentId, category, file)
    if (valueRef.current.photos[category]?.url !== placeholder.url) {
      discardIfOrphaned(update.pathname)
      return
    }
    onChange({
      photos: {
        ...valueRef.current.photos,
        [category]: { ...placeholder, ...update },
      },
    })
  }

  function remove(category: PhotoCategoryId) {
    const photo = valueRef.current.photos[category]
    if (!photo) return
    const photos = { ...valueRef.current.photos }
    delete photos[category]
    onChange({ photos })
    URL.revokeObjectURL(photo.url)
    discardIfOrphaned(photo.pathname)
  }

  function setNote(category: PhotoCategoryId, note: string) {
    onChange({ notes: { ...value.notes, [category]: note } })
  }

  function addAdditional(files: FileList) {
    if (!subjectId || !documentId) {
      throw new Error("Subject or document ID is missing");
    }

    const picked = Array.from(files).map((file) => ({
      file,
      placeholder: toPhotoUpload(file),
    }))
    onChange({
      additional: [
        ...valueRef.current.additional,
        ...picked.map((p) => p.placeholder),
      ],
    })

    for (const { file, placeholder } of picked) {
      if (placeholder.error) continue
      void (async () => {
        const update = await uploadFile(subjectId, documentId, "ADDITIONAL", file)
        const list = valueRef.current.additional
        const index = list.findIndex((p) => p.url === placeholder.url)
        if (index === -1) {
          discardIfOrphaned(update.pathname)
          return
        }
        const next = [...list]
        next[index] = { ...next[index], ...update }
        onChange({ additional: next })
      })()
    }
  }

  function removeAdditional(index: number) {
    const photo = valueRef.current.additional[index]
    if (!photo) return
    onChange({
      additional: valueRef.current.additional.filter((_, i) => i !== index),
    })
    URL.revokeObjectURL(photo.url)
    discardIfOrphaned(photo.pathname)
  }

  function renderGroup(group: string) {
    const categories = photoCategories.filter(
      (c) =>
        c.group === group &&
        (c.id === "ADDITIONAL" ||
          isPhotoCategoryVisible(c.id, vehicleCategory, hasTachograph))
    )
    return (
      <div className="grid grid-cols-1 gap-3">
        {categories.map((category) =>
          category.id === "ADDITIONAL" ? (
            <AdditionalCell
              key={category.id}
              onAdd={addAdditional}
              onRemove={removeAdditional}
              onViewFullscreen={setFullscreen}
              photos={value.additional}
            />
          ) : (
            <CategoryCell
              categoryId={category.id}
              key={category.id}
              label={category.label}
              note={value.notes[category.id] ?? ""}
              onNoteChange={(note) => setNote(category.id, note)}
              onPick={(file) => pick(category.id, file)}
              onRemove={() => remove(category.id)}
              onViewFullscreen={() =>
                setFullscreen(value.photos[category.id]?.url ?? null)
              }
              photo={value.photos[category.id]}
              required
            />
          )
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <FormCard title="Основні фотоматеріали">
        <p className="mb-4 text-sm text-muted-foreground">
          Обов'язкові: панель приладів, спереду, ззаду, ліворуч, праворуч. Для
          M2/M3 додається салон, для ТЗ з тахографом — тахограф і табличка
          тахографа. Усього — до 15 фото.
        </p>
        {renderGroup(photoGroups[0])}
      </FormCard>
      <FormCard title="Фото за умовами експлуатації">
        {renderGroup(photoGroups[1])}
      </FormCard>
      <FormCard title="Додаткові фотоматеріали">
        {renderGroup(photoGroups[2])}
        <p className="mt-4 text-xs text-muted-foreground">
          JPEG/JPG/PNG, від 640×480 до 4096×3072, до 5 МБ. АРМ зберігає
          оригінал і готує нормалізовану копію для ЄДРТЗ.
        </p>
      </FormCard>

      {fullscreen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setFullscreen(null)}
          onKeyDown={(e) => e.key === "Escape" && setFullscreen(null)}
          role="presentation"
        >
          <Button
            aria-label="Закрити повноекранний перегляд"
            className="absolute top-4 right-4 text-white"
            onClick={() => setFullscreen(null)}
            size="icon"
            type="button"
            variant="ghost"
          >
            <X />
          </Button>
          <img
            alt=""
            className="max-h-full max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
            src={fullscreen}
          />
        </div>
      )}
    </div>
  )
}
